import { useAnalytics } from '@app/analytics/use-analytics';
import {
  CreatorLabBaseProperties,
  CreatorLabContentCopiedProperties,
  CreatorLabContentGeneratedOnclickProperties,
  CreatorLabContentRegeneratedProperties,
  CreatorLabContentType,
  CreatorLabContentTypeActionProperties,
  CreatorLabContentTypeActionType,
  CreatorLabContext,
  CreatorLabFeedbackSubmittedProperties,
  CreatorLabScrollPercentage,
  CreatorLabScrollPercentageProperties,
  CreatorLabThumbsDownClickedProperties,
  CreatorLabThumbsUpClickedProperties,
} from '@app/routes/content/analytics/events';
import { useAppSelector } from '@app/store';
import { useCallback, useMemo } from 'react';

import { selectRecording } from '../../../selector';
import { getVideoType } from '../../../utils';
import { ContentType, EmailContentGenerationAttributes } from '../domain';
import {
  selectContentGenerationAttributes,
  selectContentRepurposingState,
  selectCreatorLabContentHistory,
  selectCreatorLabSelectedContent,
  selectCreatorLabSelectedContentType,
  selectCurrentGeneratedContent,
  selectHaveContentGenerationAttributesChanged,
  selectToneOfVoiceConfig,
} from '../selector';

/**
 * Maps the internal ContentType to analytics-friendly content type string
 */
const mapContentType = (
  contentType: 'SOCIAL_POST' | 'BLOG_POST' | 'EMAIL',
): CreatorLabContentType => {
  const mapping: Record<string, CreatorLabContentType> = {
    SOCIAL_POST: 'social_media',
    BLOG_POST: 'blog',
    EMAIL: 'email',
  };
  return mapping[contentType] || 'social_media';
};

export const useCreatorLabAnalytics = () => {
  const { track } = useAnalytics();
  const recording = useAppSelector(selectRecording);
  const selectedContentType = useAppSelector(
    selectCreatorLabSelectedContentType,
  );
  const attributes = useAppSelector(selectContentGenerationAttributes);
  const toneOfVoiceConfig = useAppSelector(selectToneOfVoiceConfig);
  const haveAttributesChanged = useAppSelector(
    selectHaveContentGenerationAttributesChanged,
  );
  const contentRepurposingState = useAppSelector(selectContentRepurposingState);
  const currentGeneratedContent = useAppSelector(selectCurrentGeneratedContent);
  const contentHistory = useAppSelector(selectCreatorLabContentHistory);
  const selectedContent = useAppSelector(selectCreatorLabSelectedContent);

  // Determine if user selected a non-latest history item
  const isHistorySelected = useMemo(() => {
    const history = contentHistory[selectedContentType];
    if (
      !history ||
      history.status !== 'success' ||
      !history.history ||
      history.history.length === 0
    ) {
      return false;
    }
    const latestEntryId = history.history[0]?.id;
    const selectedId = selectedContent?.id;
    // History is considered "selected" if user is not on the latest entry
    return selectedId !== null && selectedId !== latestEntryId;
  }, [contentHistory, selectedContentType, selectedContent]);

  const baseProperties = useMemo((): CreatorLabBaseProperties & {
    recordingDuration?: number;
  } => {
    return {
      contentType: mapContentType(selectedContentType),
      videoType: getVideoType(recording),
      // Include recordingDuration like useRecordingAnalytics does
      recordingDuration: recording?.duration,
    };
  }, [selectedContentType, recording]);

  // Memoized context object - reused by multiple tracking functions
  const context = useMemo((): CreatorLabContext => {
    // Get tone name
    const toneOfVoiceId = attributes.toneOfVoiceId;
    const toneName =
      toneOfVoiceConfig.data?.find(t => t.key === toneOfVoiceId)?.toneOfVoice ||
      toneOfVoiceId ||
      '';

    // Get topic name from content repurposing state
    const contentRepurposingData = contentRepurposingState?.data;
    const topics =
      contentRepurposingData?.eligibility === 'ELIGIBLE'
        ? contentRepurposingData.topics.value
        : null;

    // For EMAIL, use first topic; for others, use selected topic
    let topicName = '';
    if (selectedContentType === 'EMAIL') {
      topicName = topics?.[0]?.topic || '';
    } else {
      const selectedTopic = topics?.find(t => t.id === attributes.topicId);
      topicName = selectedTopic?.topic || '';
    }

    // Build base context
    const baseContext: CreatorLabContext = {
      topicSelected: topicName,
      toneSelected: toneName,
    };

    // Add email-specific context values
    if (selectedContentType === 'EMAIL') {
      const emailAttributes = attributes as EmailContentGenerationAttributes;
      baseContext.recipientSelected =
        emailAttributes.emailAudienceId || undefined;
      baseContext.emailTypeSelected = emailAttributes.emailTypeId || undefined;
    }

    return baseContext;
  }, [
    attributes,
    toneOfVoiceConfig.data,
    selectedContentType,
    contentRepurposingState,
  ]);

  const trackTopicSelected = useCallback(
    (topicName: string) => {
      track('creator_lab_topic_selected', {
        ...baseProperties,
        topicSelected: topicName,
      });
    },
    [track, baseProperties],
  );

  const trackToneSelected = useCallback(
    (toneName: string) => {
      track('creator_lab_tone_selected', {
        ...baseProperties,
        toneSelected: toneName,
      });
    },
    [track, baseProperties],
  );

  const trackEmailTypeSelected = useCallback(
    (emailTypeId: string) => {
      track('creator_lab_email_type_selected', {
        ...baseProperties,
        emailTypeSelected: emailTypeId,
      });
    },
    [track, baseProperties],
  );

  const trackEmailRecipientSelected = useCallback(
    (recipientId: string) => {
      track('creator_lab_email_recipient_selected', {
        ...baseProperties,
        recipientSelected: recipientId,
      });
    },
    [track, baseProperties],
  );

  const trackContentGeneratedOnclick = useCallback(() => {
    // Determine what changed by comparing with generated content
    let topicChanged: 'yes' | 'no' = 'no';
    let toneChanged: 'yes' | 'no' = 'no';
    let recipientChanged: 'yes' | 'no' | undefined;
    let emailTypeChanged: 'yes' | 'no' | undefined;

    if (
      currentGeneratedContent &&
      'toneVoiceId' in currentGeneratedContent &&
      'topics' in currentGeneratedContent
    ) {
      // Check if tone changed
      if (attributes.toneOfVoiceId !== currentGeneratedContent.toneVoiceId) {
        toneChanged = 'yes';
      }

      // Check if topic changed (for non-email content types)
      if (
        selectedContentType === 'SOCIAL_POST' ||
        selectedContentType === 'BLOG_POST'
      ) {
        const currentTopicId = attributes.topicId;
        const contentTopicId = currentGeneratedContent.topics[0] ?? null;
        if (currentTopicId !== contentTopicId) {
          topicChanged = 'yes';
        }
      }

      // Check email-specific changes
      if (selectedContentType === 'EMAIL') {
        const emailAttributes = attributes as EmailContentGenerationAttributes;
        const contentContext = currentGeneratedContent.context;

        recipientChanged =
          emailAttributes.emailAudienceId !== contentContext?.recipientTypeId
            ? 'yes'
            : 'no';
        emailTypeChanged =
          emailAttributes.emailTypeId !== contentContext?.emailTypeId
            ? 'yes'
            : 'no';
      }
    }

    // Build payload
    const payload: CreatorLabContentGeneratedOnclickProperties = {
      ...baseProperties,
      topicChanged,
      toneChanged,
      context,
    };

    // Add email-specific change flags
    if (selectedContentType === 'EMAIL') {
      payload.recipientChanged = recipientChanged;
      payload.emailTypeChanged = emailTypeChanged;
    }

    track('creator_lab_content_generated_onclick', payload);
  }, [
    track,
    baseProperties,
    context,
    attributes,
    selectedContentType,
    currentGeneratedContent,
  ]);

  const trackContentRegenerated = useCallback(() => {
    const payload: CreatorLabContentRegeneratedProperties = {
      ...baseProperties,
      historySelected: isHistorySelected ? 'yes' : 'no',
      context,
    };

    track('creator_lab_content_regenerated', payload);
  }, [track, baseProperties, context, isHistorySelected]);

  const trackContentCopied = useCallback(
    (optionType?: 'option_1' | 'option_2') => {
      const payload: CreatorLabContentCopiedProperties = {
        ...baseProperties,
        context,
      };

      // Only include optionType for social media content
      if (selectedContentType === 'SOCIAL_POST' && optionType) {
        payload.optionType = optionType;
      }

      track('creator_lab_content_copied', payload);
    },
    [track, baseProperties, context, selectedContentType],
  );

  /**
   * Tracks the appropriate event based on whether content is being generated
   * with new attributes or regenerated with same attributes
   */
  const trackGenerateOrRegenerate = useCallback(() => {
    if (haveAttributesChanged) {
      trackContentGeneratedOnclick();
    } else {
      trackContentRegenerated();
    }
  }, [
    haveAttributesChanged,
    trackContentGeneratedOnclick,
    trackContentRegenerated,
  ]);

  /**
   * Tracks when user clicks thumbs up on AI feedback
   */
  const trackThumbsUpClicked = useCallback(
    (optionType?: 'option_1' | 'option_2') => {
      const payload: CreatorLabThumbsUpClickedProperties = {
        ...baseProperties,
        context,
      };

      // Only include optionType for social media content
      if (selectedContentType === 'SOCIAL_POST' && optionType) {
        payload.optionType = optionType;
      }

      track('creator_lab_thumbs_up_clicked', payload);
    },
    [track, baseProperties, context, selectedContentType],
  );

  /**
   * Tracks when user clicks thumbs down on AI feedback
   */
  const trackThumbsDownClicked = useCallback(
    (optionType?: 'option_1' | 'option_2') => {
      const payload: CreatorLabThumbsDownClickedProperties = {
        ...baseProperties,
      };

      // Only include optionType for social media content
      if (selectedContentType === 'SOCIAL_POST' && optionType) {
        payload.optionType = optionType;
      }

      track('creator_lab_thumbs_down_clicked', payload);
    },
    [track, baseProperties, selectedContentType],
  );

  /**
   * Tracks when user submits feedback (after thumbs down)
   */
  const trackFeedbackSubmitted = useCallback(
    (feedbackText?: string, optionType?: 'option_1' | 'option_2') => {
      const payload: CreatorLabFeedbackSubmittedProperties = {
        ...baseProperties,
        context,
      };

      // Only include optionType for social media content
      if (selectedContentType === 'SOCIAL_POST' && optionType) {
        payload.optionType = optionType;
      }

      // Only include feedbackText if provided
      if (feedbackText) {
        payload.feedbackText = feedbackText;
      }

      track('creator_lab_feedback_submitted', payload);
    },
    [track, baseProperties, context, selectedContentType],
  );

  const trackContentTypeAction = useCallback(
    (contentType: ContentType, actionType: CreatorLabContentTypeActionType) => {
      const payload: CreatorLabContentTypeActionProperties = {
        contentType: mapContentType(contentType),
        videoType: getVideoType(recording),
        actionType,
      };

      track('creator_lab_content_type_action', payload);
    },
    [track, recording],
  );

  const trackScrollPercentage = useCallback(
    (scrollPercentage: CreatorLabScrollPercentage) => {
      const payload: CreatorLabScrollPercentageProperties = {
        contentType: mapContentType(selectedContentType),
        videoType: getVideoType(recording),
        scrollPercentage,
      };

      track('creator_lab_scroll_percentage', payload);
    },
    [track, selectedContentType, recording],
  );

  return {
    trackTopicSelected,
    trackToneSelected,
    trackEmailTypeSelected,
    trackEmailRecipientSelected,
    trackContentCopied,
    trackContentRegenerated,
    trackGenerateOrRegenerate,
    trackThumbsUpClicked,
    trackThumbsDownClicked,
    trackFeedbackSubmitted,
    trackContentTypeAction,
    trackScrollPercentage,
  };
};

// Export helper functions for use in sagas (non-hook context)
export { mapContentType };
